import { FC, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Loading } from '@carbon/react';
import { PlainLink } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { EmptyState } from 'components/empty-state';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import DocumentSvg from 'images/srp-illustration-document.svg';
import styles from './page.module.scss';

interface SupportingFile extends Record<string, unknown> {
  id: number | string;
  fileName: string | null;
  attachmentHref: string | null;
  externalId: string | null;
  createdAt: string | null;
}

type SupportingFilesResponse = SupportingFile[];

const columns: DataTableColumnDef<SupportingFile>[] = [
  {
    accessorKey: 'fileName',
    header: 'File Name',
    cell: ({ row }) => {
      const label = row.original.fileName ?? `File #${row.original.id}`;
      return row.original.attachmentHref ? (
        <PlainLink
          href={row.original.attachmentHref}
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </PlainLink>
      ) : (
        label
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ row }) => row.original.createdAt ?? '--',
  },
];

interface Props {
  href: string;
}

const RoyaltyCheckSupportingFiles: FC<Props> = ({ href }) => {
  const supportingFilesAtom = useMemo(
    () => createResourceAtom<SupportingFilesResponse>(href),
    [href]
  );
  const resource = useAtomValue(supportingFilesAtom);
  const { loading, error, data } = formatResource(resource);
  const files = data ?? [];
  const isEmpty = !loading && !error && files.length === 0;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Supporting Files</h3>
      {loading && (
        <Loading
          description="Loading supporting files..."
          withOverlay={false}
          small
        />
      )}
      {error && <p>Unable to load supporting files</p>}
      {isEmpty && (
        <EmptyState
          icon={DocumentSvg}
          headerText="No Files Available"
          helperText="This royalty check doesn't have any supporting files."
        />
      )}
      {!loading && !error && files.length > 0 && (
        <DataTable<SupportingFile>
          data={files}
          columns={columns}
          getRowId={(row) => String(row.id)}
          config={{
            globalSearch: false,
            columnFilters: false,
            columnReordering: false,
            columnResizing: false,
            columnPinning: false,
            columnVisibility: false,
            sorting: false,
            export: false,
            hideFooter: true,
          }}
        />
      )}
    </section>
  );
};

export { RoyaltyCheckSupportingFiles };
